import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const UTM_STORAGE_KEY = 'adr:utm-selling-plan-context';
const BEST_PLAN_STORAGE_KEY = 'adr:best-selling-plan-record';

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeKey(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * @param {string | number | null | undefined} value
 * @returns {string}
 */
function normalizePlanId(value) {
  return String(value ?? '').trim();
}

/**
 * @param {PlanAllocation} allocation
 * @param {string | number | null | undefined} sellingPlanId
 * @returns {boolean}
 */
function allocationMatchesSellingPlanId(allocation, sellingPlanId) {
  const normalizedId = normalizePlanId(sellingPlanId);
  return (
    normalizePlanId(allocation.sellingPlanId) === normalizedId ||
    normalizePlanId(allocation.sellingPlanIdNumeric) === normalizedId
  );
}

/**
 * Name-based BNPL heuristic, kept as a second line of defense. The primary
 * gate lives in Liquid (channel-selling-plan-selector.liquid): only plans with
 * `selling_plan.recurring_deliveries: true` are serialized into the
 * data-channel-selling-plan-variants payload, so pre-order/deferred-purchase
 * and BNPL plans normally never reach this script at all.
 *
 * @param {string} name
 * @returns {boolean}
 */
function isInstallmentPlan(name) {
  const n = normalizeKey(name);
  return /\b(klarna|affirm|after\s*pay|shop\s*pay\s*install|installment|split\s*pay|4\s*interest-free|4\s*payments|sezzle|quadpay|zip\s*pay)\b/.test(
    n
  );
}

/**
 * @typedef {Object} PlanAllocation
 * @property {string | number} sellingPlanId
 * @property {string} sellingPlanIdNumeric
 * @property {string} sellingPlanName
 * @property {string} deliveryFrequency
 * @property {string | null} description
 * @property {boolean} [isDefault]
 * @property {number} [position]
 * @property {string} groupId
 * @property {string} groupName
 * @property {number} price
 * @property {number} [recurringPrice] - Price after the first cycle (differs from price for e.g. 100%-off-first-order plans)
 * @property {number | null} compareAtPrice
 */

/**
 * @typedef {Object} ChannelMapping
 * @property {string} channel_identifier
 * @property {string} selling_plan_group_name
 * @property {boolean} [enabled]
 * @property {number} [priority]
 */

/**
 * @typedef {Object} ChannelConfig
 * @property {number} [utmPersistenceTtlDays]
 * @property {ChannelMapping[]} mappings
 */

/**
 * @typedef {Object} VariantPlansPayload
 * @property {Record<string, PlanAllocation[]>} variants
 * @property {Record<string, number>} [basePrices]
 * @property {Record<string, number | null>} [baseCompareAtPrices]
 * @property {Record<string, string[]>} [groupPlanOrder]
 * @property {{ id: string; name: string }[]} groups
 */

/**
 * @typedef {Object} BestPlanRecord
 * @property {string} sellingPlanId
 * @property {string} sellingPlanIdNumeric
 * @property {string} groupNameNormalized
 * @property {number} price
 * @property {number} updatedAt
 */

/**
 * @param {PlanAllocation[]} allocations
 * @returns {PlanAllocation[]}
 */
function filterRecurringAllocations(allocations) {
  return allocations.filter((a) => !isInstallmentPlan(a.sellingPlanName));
}

/**
 * @param {PlanAllocation[]} allocations
 * @returns {PlanAllocation | undefined}
 */
function pickCheapestAllocation(allocations) {
  const usable = filterRecurringAllocations(allocations);
  if (!usable.length) return undefined;
  return usable.reduce((best, cur) => (Number(cur.price) < Number(best.price) ? cur : best));
}

/**
 * @param {string} raw
 * @returns {ChannelConfig}
 */
function parseConfig(raw) {
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return { mappings: [], utmPersistenceTtlDays: 30 };
    }
    return {
      utmPersistenceTtlDays: Number(parsed.utmPersistenceTtlDays) || 30,
      mappings: Array.isArray(parsed.mappings) ? parsed.mappings : [],
    };
  } catch {
    return { mappings: [], utmPersistenceTtlDays: 30 };
  }
}

/**
 * Shared config fetch, one request per URL. Multiple selector instances on a
 * page (e.g. landing pages with many buy-buttons blocks) connect in the same
 * tick, before any response can populate the HTTP cache — without this each
 * instance fired its own fetch. The parsed config is read-only downstream, so
 * instances can safely share the object. Resolves to null on a non-OK
 * response (callers keep their default config, matching the previous
 * per-instance behavior); a thrown network error clears the cache entry so a
 * later connect retries.
 * @type {Map<string, Promise<ChannelConfig | null>>}
 */
const configPromises = new Map();

/**
 * @param {string} url
 * @returns {Promise<ChannelConfig | null>}
 */
function loadSharedConfig(url) {
  let promise = configPromises.get(url);
  if (!promise) {
    promise = fetch(url)
      .then((response) => (response.ok ? response.text() : null))
      .then((text) => (text === null ? null : parseConfig(text)))
      .catch((error) => {
        configPromises.delete(url);
        throw error;
      });
    configPromises.set(url, promise);
  }
  return promise;
}

class ChannelSellingPlanRoot extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {HTMLElement | null} */
  #section = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {ChannelConfig} */
  #config = { mappings: [], utmPersistenceTtlDays: 30 };

  /** @type {VariantPlansPayload | null} */
  #payload = null;

  /** @type {(event: Event) => void} */
  #onVariantUpdateBound = this.#onVariantUpdate.bind(this);

  /** @type {(event: Event) => void} */
  #onPurchaseTypeChangeBound = this.#onPurchaseTypeChange.bind(this);

  /** @type {(event: Event) => void} */
  #onSelectChangeBound = this.#onSelectChange.bind(this);

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#section = this.closest('.shopify-section, dialog');
    this.#form = this.closest('form');
    this.#payload = this.#readPayload();

    this.#placeMainPriceAboveVariantPicker();

    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdateBound, {
      signal: this.#abortController.signal,
    });

    const purchaseInputs = this.querySelectorAll('[data-channel-purchase-type]');
    for (const input of purchaseInputs) {
      input.addEventListener('change', this.#onPurchaseTypeChangeBound, {
        signal: this.#abortController.signal,
      });
    }

    this.querySelector('[data-channel-selling-plan-select]')?.addEventListener(
      'change',
      this.#onSelectChangeBound,
      { signal: this.#abortController.signal }
    );

    void this.#init();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#section = null;
    this.#form = null;
    this.#payload = null;
  }

  /**
   * @returns {VariantPlansPayload | null}
   */
  #readPayload() {
    const el = this.querySelector('script[data-channel-selling-plan-variants]');
    if (!el?.textContent) return null;
    try {
      return /** @type {VariantPlansPayload} */ (JSON.parse(el.textContent));
    } catch {
      return null;
    }
  }

  async #init() {
    await this.#loadConfig();
    this.#persistUtmFromUrl();
    this.#placeMainPriceAboveVariantPicker();
    await this.#refreshPlanState();
  }

  /**
   * Moves the buy-buttons synced price above the variant picker (Model
   * selection). Price lives in buy-buttons for plan sync, but should render
   * above Model in the PDP stack. Dedupes after section morphs.
   */
  #placeMainPriceAboveVariantPicker() {
    const productId = this.dataset.productId ?? '';
    const prices = Array.from(
      this.#section?.querySelectorAll(
        `product-price.buy-buttons-block__price[data-product-id="${productId}"]`
      ) ?? []
    ).filter((el) => el instanceof HTMLElement);

    if (!prices.length) return;

    const [price, ...duplicates] = prices;
    for (const duplicate of duplicates) {
      duplicate.remove();
    }

    const content = price.closest('.group-block-content');
    if (!(content instanceof HTMLElement)) return;

    const variantPicker = content.querySelector('variant-picker');
    if (!(variantPicker instanceof HTMLElement)) return;

    if (variantPicker.previousElementSibling === price) return;

    variantPicker.before(price);
  }

  async #loadConfig() {
    const url = this.dataset.configUrl;
    if (!url) return;
    try {
      const config = await loadSharedConfig(url);
      if (config === null) return;
      this.#config = config;
    } catch {
      this.#config = { mappings: [], utmPersistenceTtlDays: 30 };
    }
  }

  #ttlMs() {
    const days = this.#config.utmPersistenceTtlDays ?? 30;
    return Math.max(1, days) * 86400000;
  }

  /**
   * @returns {Record<string, string>}
   */
  #readUtmFromSearchParams() {
    const search = new URLSearchParams(window.location.search);
    /** @type {Record<string, string>} */
    const out = {};
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'affiliate', 'sub_channel'];
    for (const key of keys) {
      const v = search.get(key);
      if (v) out[key] = v;
    }
    return out;
  }

  #persistUtmFromUrl() {
    const fromUrl = this.#readUtmFromSearchParams();
    const prev = this.#loadStoredUtm();
    const merged = { ...prev, ...fromUrl, ts: Date.now() };
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(merged));
    } catch {
      /* ignore quota / private mode */
    }
  }

  /**
   * @returns {Record<string, string | number>}
   */
  #loadStoredUtm() {
    try {
      const raw = localStorage.getItem(UTM_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      const ts = Number(parsed.ts) || 0;
      if (Date.now() - ts > this.#ttlMs()) {
        localStorage.removeItem(UTM_STORAGE_KEY);
        return {};
      }
      return /** @type {Record<string, string | number>} */ (parsed);
    } catch {
      return {};
    }
  }

  /**
   * @param {Record<string, unknown>} source
   * @returns {string[]}
   */
  #channelKeysFromSource(source) {
    /** @type {string[]} */
    const keys = [];
    const pushNorm = (v) => {
      const n = normalizeKey(v);
      if (!n || keys.includes(n)) return;
      keys.push(n);
    };

    const orderedParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'affiliate', 'sub_channel'];
    for (const p of orderedParams) {
      const value = source[p];
      if (typeof value === 'string' && value) {
        pushNorm(value);
      }
    }

    const src = normalizeKey(source.utm_source);
    const med = normalizeKey(source.utm_medium);
    // Everflow affiliate traffic reports as utm_medium=influencers as of 2026-08-19.
    // The legacy 'affiliate' value is still accepted because stored attribution
    // persists for utmPersistenceTtlDays (30) — drop that branch after 2026-09-18.
    // The pushed key stays 'affiliate': that is the internal channel identifier
    // the config mappings resolve against, and it is unchanged.
    if (src === 'everflow' && (med === 'influencers' || med === 'affiliate')) {
      pushNorm('affiliate');
    }

    return keys;
  }

  /**
   * @returns {string[]}
   */
  #getUrlChannelKeys() {
    const fromUrl = this.#readUtmFromSearchParams();
    return this.#channelKeysFromSource(fromUrl);
  }

  /**
   * @returns {string[]}
   */
  #getStoredChannelKeys() {
    const stored = this.#loadStoredUtm();
    return this.#channelKeysFromSource(stored);
  }

  /**
   * @returns {BestPlanRecord | null}
   */
  #loadBestPlan() {
    try {
      const raw = localStorage.getItem(BEST_PLAN_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return /** @type {BestPlanRecord} */ (parsed);
    } catch {
      return null;
    }
  }

  /**
   * @param {BestPlanRecord} record
   */
  #maybeSaveBestPlan(record) {
    const prev = this.#loadBestPlan();
    if (!prev || Number(record.price) <= Number(prev.price)) {
      try {
        localStorage.setItem(BEST_PLAN_STORAGE_KEY, JSON.stringify(record));
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * @returns {string}
   */
  #currentVariantId() {
    const fromForm = this.#form?.querySelector('input[name="id"]')?.value;
    return fromForm ? String(fromForm) : '';
  }

  /**
   * @param {string} variantId
   * @returns {PlanAllocation[]}
   */
  #allocationsForVariant(variantId) {
    if (!this.#payload?.variants || !variantId) return [];
    const list = this.#payload.variants[variantId];
    return Array.isArray(list) ? list : [];
  }

  /**
   * Companion mode: plan data comes from a companion product (the unit's
   * filter) and the hidden inputs are filter_variant_id/filter_selling_plan,
   * which product-form.js turns into a two-line-item add.
   */
  get #isCompanionMode() {
    return this.dataset.companionMode === 'true';
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #sellingPlanInput() {
    return this.querySelector('[data-channel-selling-plan-input]');
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #companionVariantInput() {
    return this.querySelector('[data-channel-filter-variant-input]');
  }

  /**
   * Direct-subscription only — marks the line as Loop-priced so IOM's
   * `exclude_cart_property_keys` leaves it unstamped and undiscounted. Null in
   * companion mode, where IOM owns the paired filter line's discount.
   * @returns {HTMLInputElement | null}
   */
  #loopPricedInput() {
    return this.querySelector('[data-channel-loop-priced-input]');
  }

  /**
   * @returns {HTMLSelectElement | null}
   */
  #planSelect() {
    return this.querySelector('[data-channel-selling-plan-select]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #frequencyWrap() {
    return this.querySelector('[data-channel-frequency-wrap]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #oneTimePriceElement() {
    return this.querySelector('[data-channel-one-time-price]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionPriceElement() {
    return this.querySelector('[data-channel-subscription-price]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionTitleElement() {
    return this.querySelector('[data-channel-subscription-title]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionSubtitleElement() {
    return this.querySelector('[data-channel-subscription-subtitle]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionCompareElement() {
    return this.querySelector('[data-channel-subscription-compare]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #subscriptionContentElement() {
    return this.querySelector('[data-channel-subscription-content]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #oneTimeCompareElement() {
    return this.querySelector('[data-channel-one-time-compare]');
  }

  /**
   * @returns {string}
   */
  #currencyCode() {
    return this.dataset.currencyCode || 'USD';
  }

  /**
   * Shopify money format from Liquid (`money` or `money_with_currency`),
   * chosen to match `settings.currency_code_enabled_product_pages`.
   * @returns {string}
   */
  #moneyFormat() {
    return this.dataset.moneyFormat || '${{amount}}';
  }

  /**
   * @param {number | string | null | undefined} subunits
   * @returns {string}
   */
  #formatMoney(subunits) {
    const amount = Number(subunits);
    if (!Number.isFinite(amount)) return '';

    try {
      return formatMoney(amount, this.#moneyFormat(), this.#currencyCode());
    } catch {
      const major = amount / 100;
      return `$${major.toFixed(2)}`;
    }
  }

  /**
   * @param {string} variantId
   * @returns {number | null}
   */
  #basePriceForVariant(variantId) {
    const price = this.#payload?.basePrices?.[variantId];
    return Number.isFinite(Number(price)) ? Number(price) : null;
  }

  /**
   * @param {string} variantId
   * @returns {number | null}
   */
  #baseCompareAtForVariant(variantId) {
    const value = this.#payload?.baseCompareAtPrices?.[variantId];
    return Number.isFinite(Number(value)) ? Number(value) : null;
  }

  /**
   * @param {PlanAllocation[]} allocations
   * @returns {PlanAllocation[]}
   */
  #sortAllocationsByGroupOrder(allocations) {
    if (!allocations.length) return [];
    const groupId = allocations[0].groupId;
    const order = this.#payload?.groupPlanOrder?.[groupId];
    if (!Array.isArray(order) || !order.length) {
      return [...allocations];
    }

    const rank = new Map(order.map((id, idx) => [String(id), idx]));
    return [...allocations].sort((a, b) => {
      const aIdx = rank.has(normalizePlanId(a.sellingPlanId))
        ? rank.get(normalizePlanId(a.sellingPlanId))
        : rank.get(normalizePlanId(a.sellingPlanIdNumeric));
      const bIdx = rank.has(normalizePlanId(b.sellingPlanId))
        ? rank.get(normalizePlanId(b.sellingPlanId))
        : rank.get(normalizePlanId(b.sellingPlanIdNumeric));
      const ra = aIdx ?? Number.MAX_SAFE_INTEGER;
      const rb = bIdx ?? Number.MAX_SAFE_INTEGER;
      if (ra !== rb) return ra - rb;
      return Number(a.price) - Number(b.price);
    });
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #containsHtmlMarkup(value) {
    return /<[a-z][\s\S]*>/i.test(value);
  }

  /**
   * Allowlisted tags for selling-plan description HTML
   * (parsed only via #parseAllowlistedHtmlInto — never DOMParser).
   * @type {ReadonlySet<string>}
   */
  static #ALLOWED_DESCRIPTION_TAGS = new Set([
    'P',
    'DIV',
    'SPAN',
    'UL',
    'OL',
    'LI',
    'STRONG',
    'EM',
    'B',
    'I',
    'BR',
    'H2',
    'H3',
    'H4',
    'A',
  ]);

  /**
   * @param {string} href
   * @returns {boolean}
   */
  #isSafeHref(href) {
    const trimmed = String(href ?? '').trim();
    if (!trimmed) return false;
    if (trimmed.startsWith('/')) return true;
    if (trimmed.startsWith('#') || trimmed.startsWith('?')) return true;
    return /^(https?:|mailto:)/i.test(trimmed);
  }

  /**
   * Rebuild an allowlisted copy of `node` under `parent`.
   * Used when copying parsed description trees into the body fragment.
   *
   * @param {Node} node
   * @param {ParentNode} parent
   */
  #appendSanitizedNode(node, parent) {
    if (node.nodeType === Node.TEXT_NODE) {
      parent.appendChild(document.createTextNode(node.textContent ?? ''));
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = /** @type {Element} */ (node);
    const tag = el.tagName.toUpperCase();

    if (!ChannelSellingPlanRoot.#ALLOWED_DESCRIPTION_TAGS.has(tag)) {
      for (const child of Array.from(el.childNodes)) {
        this.#appendSanitizedNode(child, parent);
      }
      return;
    }

    const clean = document.createElement(tag.toLowerCase());

    if (tag === 'A') {
      const href = el.getAttribute('href');
      if (href && this.#isSafeHref(href)) {
        clean.setAttribute('href', href);
      }
    }

    for (const child of Array.from(el.childNodes)) {
      this.#appendSanitizedNode(child, clean);
    }

    parent.appendChild(clean);
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  #decodeHtmlEntities(text) {
    // Unescape &amp; last (CodeQL js/double-escaping). Defer &#38;/&#x26; into &amp;.
    return String(text)
      .replace(/&nbsp;/gi, '\u00a0')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#(\d+);/g, (_, code) => {
        const n = Number(code);
        if (n === 38) return '&amp;';
        return String.fromCharCode(n);
      })
      .replace(/&#x([0-9a-f]+);/gi, (_, hex) => {
        const n = parseInt(hex, 16);
        if (n === 0x26) return '&amp;';
        return String.fromCharCode(n);
      })
      .replace(/&amp;/gi, '&');
  }

  /**
   * @param {HTMLElement} parent
   * @param {string} text
   */
  #appendDecodedText(parent, text) {
    if (!text) return;
    parent.appendChild(document.createTextNode(this.#decodeHtmlEntities(text)));
  }

  /**
   * Parse allowlisted HTML into `parent` without DOMParser or innerHTML
   * (CodeQL js/xss-through-dom safe for JSON payload description strings).
   *
   * @param {string} html
   * @param {HTMLElement} parent
   */
  #parseAllowlistedHtmlInto(html, parent) {
    const allowed = ChannelSellingPlanRoot.#ALLOWED_DESCRIPTION_TAGS;
    /** @type {HTMLElement[]} */
    const stack = [parent];
    let i = 0;
    const len = html.length;

    while (i < len) {
      if (html.charCodeAt(i) !== 60) {
        const nextTag = html.indexOf('<', i);
        const text = nextTag === -1 ? html.slice(i) : html.slice(i, nextTag);
        this.#appendDecodedText(stack[stack.length - 1], text);
        i = nextTag === -1 ? len : nextTag;
        continue;
      }

      const closeIdx = html.indexOf('>', i);
      if (closeIdx === -1) {
        this.#appendDecodedText(stack[stack.length - 1], html.slice(i));
        break;
      }

      const inside = html.slice(i + 1, closeIdx).trim();
      i = closeIdx + 1;
      if (!inside) continue;

      if (inside.startsWith('/')) {
        const tagName = inside
          .slice(1)
          .trim()
          .split(/\s+/)[0]
          .toLowerCase();
        if (!allowed.has(tagName.toUpperCase())) continue;

        while (stack.length > 1) {
          const top = stack[stack.length - 1];
          if (top.tagName.toLowerCase() === tagName) {
            stack.pop();
            break;
          }
          stack.pop();
        }
        continue;
      }

      const selfClosing = /\/\s*$/.test(inside);
      const openBody = selfClosing ? inside.replace(/\/\s*$/, '').trim() : inside;
      const spaceIdx = openBody.search(/\s/);
      const tagName = (spaceIdx === -1 ? openBody : openBody.slice(0, spaceIdx)).toLowerCase();
      const attrPart = spaceIdx === -1 ? '' : openBody.slice(spaceIdx);

      if (tagName === 'br') {
        if (allowed.has('BR')) {
          stack[stack.length - 1].appendChild(document.createElement('br'));
        }
        continue;
      }

      if (!allowed.has(tagName.toUpperCase())) continue;

      const el = document.createElement(tagName);

      if (tagName === 'a') {
        const hrefMatch = attrPart.match(/\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i);
        const href = hrefMatch ? (hrefMatch[2] ?? hrefMatch[3] ?? hrefMatch[4] ?? '') : '';
        if (href && this.#isSafeHref(href)) {
          el.setAttribute('href', href);
        }
      }

      stack[stack.length - 1].appendChild(el);

      if (!selfClosing) {
        stack.push(el);
      }
    }
  }

  /**
   * Parse selling-plan description once: manual allowlist HTML builder, then
   * map into title + single body fragment (no DOMParser / innerHTML).
   * Body always goes to restFragment / data-channel-subscription-content so
   * description shows once (subtitle split caused missing or duplicate copy).
   *
   * @param {string | null | undefined} html
   * @returns {{
   *   heading: string;
   *   subtitleFragment: DocumentFragment;
   *   restFragment: DocumentFragment;
   * }}
   */
  #parseDescriptionParts(html) {
    const empty = {
      heading: '',
      subtitleFragment: document.createDocumentFragment(),
      restFragment: document.createDocumentFragment(),
    };
    const source = String(html ?? '').trim();
    if (!source) return empty;

    if (!this.#containsHtmlMarkup(source)) {
      empty.restFragment.appendChild(document.createTextNode(source));
      return empty;
    }

    try {
      const wrapper = document.createElement('div');
      this.#parseAllowlistedHtmlInto(source, wrapper);

      const h2 = wrapper.querySelector('h2');
      const heading = h2?.textContent?.trim() ?? '';
      if (h2) h2.remove();

      const subtitleFragment = document.createDocumentFragment();
      const restFragment = document.createDocumentFragment();

      for (const child of Array.from(wrapper.childNodes)) {
        this.#appendSanitizedNode(child, restFragment);
      }

      return { heading, subtitleFragment, restFragment };
    } catch {
      empty.restFragment.appendChild(document.createTextNode(source));
      return empty;
    }
  }

  /**
   * @param {'one-time' | 'subscription'} purchaseType
   */
  #updateCardSelectionClasses(purchaseType) {
    const subCard = this.querySelector('[data-channel-subscription-card]');
    const oneTimeCard = this.querySelector('[data-channel-one-time-card]');
    subCard?.classList.toggle('is-selected', purchaseType === 'subscription');
    oneTimeCard?.classList.toggle('is-selected', purchaseType === 'one-time');
  }

  /**
   * @returns {HTMLElement[]}
   */
  #productPriceElements() {
    const selector = `product-price[data-product-id="${this.dataset.productId ?? ''}"]`;
    const scoped = this.#section ? Array.from(this.#section.querySelectorAll(selector)) : [];
    return /** @type {HTMLElement[]} */ (scoped.length ? scoped : Array.from(document.querySelectorAll(selector)));
  }

  /**
   * The main PDP price node, rebuilt on every purchase-type / variant / plan
   * change. It always keeps IOM's hooks (`.price` + `data-product-type="price"`)
   * \u2014 IOM is meant to paint its offer here in every mode.
   *
   * `loopOwned` marks the case where the value the theme just wrote is Loop's
   * selling-plan price (a DIRECT subscription on this product, subscription
   * selected) rather than the variant's one-time price. Those nodes carry
   * `data-channel-plan-price`, which the IOM app embed's
   * `exclude_price_selectors` names \u2014 so IOM leaves them alone and Loop's price
   * stands. Without it, IOM repaints the plan price off the variant MSRP.
   *
   * The marker is re-decided on every rebuild, so toggling to one-time drops it
   * and IOM repaints the offer price; toggling back re-adds it and Loop's price
   * returns. Verified both directions on the QC store under `?oid=17&affid=6456`.
   * No sticky state.
   *
   * @param {string} price
   * @param {{ isSale?: boolean, loopOwned?: boolean }} [options]
   * @returns {HTMLDivElement}
   */
  #buildProductPriceContainer(price, { isSale = false, loopOwned = false } = {}) {
    const container = document.createElement('div');
    container.setAttribute('ref', 'priceContainer');

    const priceGroup = document.createElement('span');
    priceGroup.setAttribute('role', 'group');

    const saleLabel = this.dataset.labelPriceSale?.trim() || 'Sale price';
    const regularLabel = this.dataset.labelPriceRegular?.trim() || 'Regular price';

    const hiddenLabel = document.createElement('span');
    hiddenLabel.className = 'visually-hidden';
    hiddenLabel.textContent = `${isSale ? saleLabel : regularLabel}\u00a0`;

    const priceEl = document.createElement('span');
    priceEl.className = 'price';
    priceEl.setAttribute('data-product-type', 'price');
    if (loopOwned) priceEl.setAttribute('data-channel-plan-price', '');
    priceEl.textContent = price;

    priceGroup.append(hiddenLabel, priceEl);
    container.appendChild(priceGroup);

    return container;
  }

  /**
   * @param {number | string | null | undefined} price
   * @param {{ isSale?: boolean, loopOwned?: boolean }} [options]
   */
  #syncMainProductPrice(price, { isSale = false, loopOwned = false } = {}) {
    const formattedPrice = this.#formatMoney(price);
    if (!formattedPrice) return;

    for (const productPrice of this.#productPriceElements()) {
      const priceContainer = productPrice.querySelector('[ref="priceContainer"]');
      priceContainer?.replaceWith(
        this.#buildProductPriceContainer(formattedPrice, { isSale, loopOwned })
      );
    }
  }

  /**
   * @param {PlanAllocation | undefined} plan
   * @param {string} variantId
   * @param {'one-time' | 'subscription'} purchaseType
   */
  #updateVisiblePricing(plan, variantId, purchaseType) {
    const oneTimeEl = this.#oneTimePriceElement();
    const subEl = this.#subscriptionPriceElement();
    const subTitleEl = this.#subscriptionTitleElement();
    const subSubtitleEl = this.#subscriptionSubtitleElement();
    const subCompareEl = this.#subscriptionCompareElement();
    const subContentEl = this.#subscriptionContentElement();
    const oneTimeCompareEl = this.#oneTimeCompareElement();

    const basePrice = this.#basePriceForVariant(variantId);
    const baseCompareAt = this.#baseCompareAtForVariant(variantId);
    // Companion mode: the plan belongs to the filter product, so the main
    // product's price never changes with the plan.
    const companion = this.#isCompanionMode;
    // The main price shows a Loop plan price only for a DIRECT subscription on
    // this product — the same condition that swaps `basePrice` for `plan.price`
    // below. In companion mode the plan belongs to the filter product and the
    // main price stays the unit's one-time price, which IOM still owns.
    const loopOwnsMainPrice = purchaseType === 'subscription' && !!plan && !companion;
    const mainPrice = loopOwnsMainPrice ? plan.price : basePrice;
    const mainCompareAt = loopOwnsMainPrice ? plan.compareAtPrice : baseCompareAt;
    const isSale =
      mainCompareAt !== null &&
      mainCompareAt !== undefined &&
      mainPrice !== null &&
      mainPrice !== undefined &&
      Number(mainCompareAt) > Number(mainPrice);
    this.#syncMainProductPrice(mainPrice, { isSale, loopOwned: loopOwnsMainPrice });

    if (oneTimeEl) {
      oneTimeEl.setAttribute('data-variant-id', variantId);
      oneTimeEl.setAttribute('data-product-id', String(this.dataset.productId || ''));
      oneTimeEl.textContent = basePrice !== null ? this.#formatMoney(basePrice) : '';
    }
    if (oneTimeCompareEl) {
      const showCompare = baseCompareAt !== null && basePrice !== null && baseCompareAt > basePrice;
      oneTimeCompareEl.textContent = showCompare ? this.#formatMoney(baseCompareAt) : '';
    }

    if (subEl) {
      // Companion mode: the subscription card leads with the UNIT price
      // (the filter's recurring price renders on its own line below), and the
      // node carries IOM's attributes — keep data-variant-id fresh or IOM
      // keeps painting the load-time variant's offer price after a variant
      // switch. Outside companion mode the node holds the PLAN price and must
      // never carry IOM attributes (see the snippet's IOM contract comment).
      if (companion) {
        subEl.setAttribute('data-variant-id', variantId);
        subEl.setAttribute('data-product-id', String(this.dataset.productId || ''));
      }
      subEl.textContent = companion
        ? basePrice !== null
          ? this.#formatMoney(basePrice)
          : ''
        : plan
          ? this.#formatMoney(plan.price)
          : '';
    }

    const parsed = this.#parseDescriptionParts(plan?.description);
    if (subTitleEl) {
      const fallback = subTitleEl.dataset.defaultLabel || '';
      subTitleEl.textContent = parsed.heading || plan?.groupName || fallback;
    }
    if (subSubtitleEl) {
      const fallback = subSubtitleEl.dataset.defaultSubtitle || '';
      subSubtitleEl.replaceChildren();
      if (parsed.subtitleFragment.childNodes.length) {
        subSubtitleEl.append(parsed.subtitleFragment);
      } else if (fallback) {
        subSubtitleEl.textContent = fallback;
      }
    }
    if (subCompareEl) {
      // Companion mode: the card shows the unit price, so a filter-plan
      // compare-at would mislead — leave it empty.
      //
      // Direct mode: strike the ONE-TIME price for this variant — what the
      // shopper pays by NOT subscribing — so the card reads "$171.99 $146.19"
      // and the plan's saving is legible. This matters more now that IOM is
      // excluded from this node (data-channel-plan-price): it no longer paints
      // its own strike-through here, so without this the card showed a bare
      // number with nothing to compare it to.
      //
      // The variant's own compare-at is only a fallback for a missing base
      // price. Preferring it would overstate the saving on a product that is
      // already marked down, because the plan discount comes off the sale
      // price, not the MSRP.
      const oneTimePrice = basePrice ?? plan?.compareAtPrice ?? null;
      const showCompare =
        !companion &&
        oneTimePrice !== null &&
        plan?.price != null &&
        Number(oneTimePrice) > Number(plan.price);
      subCompareEl.textContent = showCompare ? this.#formatMoney(oneTimePrice) : '';
    }
    if (subContentEl) {
      subContentEl.replaceChildren();
      if (parsed.restFragment.childNodes.length) {
        subContentEl.append(parsed.restFragment);
      }
    }

    this.#updateCardSelectionClasses(purchaseType);
  }

  /**
   * @returns {'one-time' | 'subscription'}
   */
  #purchaseType() {
    if (this.dataset.requiresSellingPlan === 'true') {
      return 'subscription';
    }
    const checked = this.querySelector('[data-channel-purchase-type]:checked');
    const v = checked?.getAttribute('value');
    return v === 'subscription' ? 'subscription' : 'one-time';
  }

  /**
   * @param {PlanAllocation[]} groupAllocs
   * @param {string} defaultPlanId
   */
  #selectDefaultPlanInGroup(groupAllocs, defaultPlanId) {
    const usable = filterRecurringAllocations(groupAllocs);
    if (!usable.length) return undefined;

    const explicitDefault = usable.find((a) => a.isDefault === true);
    if (explicitDefault) return explicitDefault;

    const normDefault = normalizeKey(defaultPlanId);
    if (normDefault) {
      const match = usable.find(
        (a) =>
          normalizeKey(a.sellingPlanId) === normDefault ||
          normalizeKey(a.sellingPlanIdNumeric) === normDefault ||
          a.sellingPlanId === defaultPlanId ||
          a.sellingPlanIdNumeric === defaultPlanId
      );
      if (match) return match;
    }
    const orderedByPosition = [...usable].sort((a, b) => {
      const pa = Number(a.position ?? 0);
      const pb = Number(b.position ?? 0);
      if (pa !== pb) return pa - pb;
      return 0;
    });
    if (orderedByPosition[0] && Number(orderedByPosition[0].position ?? 0) > 0) {
      return orderedByPosition[0];
    }

    const ordered = this.#sortAllocationsByGroupOrder(usable);
    return ordered[0] ?? pickCheapestAllocation(usable);
  }

  /**
   * @param {string} variantId
   * @returns {{ allocations: PlanAllocation[]; selected: PlanAllocation | undefined }}
   */
  #resolveSubscriptionSelection(variantId) {
    const all = this.#allocationsForVariant(variantId);
    const recurring = filterRecurringAllocations(all);
    const defaultPlanId = this.dataset.defaultSellingPlanId ?? '';

    if (!recurring.length) {
      return { allocations: [], selected: undefined };
    }

    const urlKeys = this.#getUrlChannelKeys();
    const storedKeys = this.#getStoredChannelKeys();
    const mappings = this.#config.mappings.filter((m) => m.enabled !== false);

    /**
     * @param {string[]} keys
     * @returns {{ allocations: PlanAllocation[]; selected: PlanAllocation | undefined } | undefined}
     */
    const resolveFromKeys = (keys) => {
      /** @type {Map<string, { priority: number; allocs: PlanAllocation[] }>} */
      const triggered = new Map();

      for (const key of keys) {
        for (const m of mappings) {
          if (normalizeKey(m.channel_identifier) === 'default') continue;
          if (normalizeKey(m.channel_identifier) !== key) continue;
          const gNorm = normalizeKey(m.selling_plan_group_name);
          const inGroup = recurring.filter((a) => normalizeKey(a.groupName) === gNorm);
          if (!inGroup.length) continue;
          const priority = Number(m.priority) || 0;
          const prev = triggered.get(gNorm);
          if (!prev) {
            triggered.set(gNorm, { priority, allocs: [...inGroup] });
          } else {
            prev.priority = Math.max(prev.priority, priority);
            const ids = new Set(prev.allocs.map((a) => a.sellingPlanId));
            for (const a of inGroup) {
              if (!ids.has(a.sellingPlanId)) prev.allocs.push(a);
            }
          }
        }
      }

      if (!triggered.size) return undefined;
      /** @type {{ gNorm: string; minPrice: number; priority: number; allocs: PlanAllocation[] }[]} */
      const rows = [];
      for (const [gNorm, { priority, allocs }] of triggered) {
        const cheapest = pickCheapestAllocation(allocs);
        if (!cheapest) continue;
        rows.push({
          gNorm,
          minPrice: Number(cheapest.price),
          priority,
          allocs,
        });
      }
      if (!rows.length) return undefined;
      rows.sort((a, b) => {
        if (a.minPrice !== b.minPrice) return a.minPrice - b.minPrice;
        return b.priority - a.priority;
      });
      const winner = rows[0];
      const selected =
        this.#selectDefaultPlanInGroup(winner.allocs, defaultPlanId) ?? pickCheapestAllocation(winner.allocs);
      return { allocations: winner.allocs, selected };
    };

    const stored = this.#loadBestPlan();
    /** @type {{ allocations: PlanAllocation[]; selected: PlanAllocation | undefined } | undefined} */
    let fromRetainedBest;
    if (stored?.sellingPlanId) {
      const byId = recurring.find(
        (a) =>
          allocationMatchesSellingPlanId(a, stored.sellingPlanId) ||
          allocationMatchesSellingPlanId(a, stored.sellingPlanIdNumeric)
      );
      if (byId) {
        const sameGroup = recurring.filter((a) => normalizeKey(a.groupName) === normalizeKey(byId.groupName));
        fromRetainedBest = { allocations: sameGroup, selected: byId };
      }
    }

    if (!fromRetainedBest?.selected && stored?.groupNameNormalized) {
      const inGroup = recurring.filter((a) => normalizeKey(a.groupName) === stored.groupNameNormalized);
      if (inGroup.length) {
        const selected = this.#selectDefaultPlanInGroup(inGroup, defaultPlanId) ?? pickCheapestAllocation(inGroup);
        fromRetainedBest = { allocations: inGroup, selected };
      }
    }

    // If URL has explicit UTMs and we also have a retained best, keep the lower priced plan.
    // Ties prefer URL so QA / utm_source links stay deterministic.
    const fromUrlUtm = resolveFromKeys(urlKeys);
    if (fromUrlUtm?.selected && fromRetainedBest?.selected) {
      if (Number(fromUrlUtm.selected.price) <= Number(fromRetainedBest.selected.price)) {
        return fromUrlUtm;
      }
      return fromRetainedBest;
    }
    if (fromUrlUtm?.selected) {
      return fromUrlUtm;
    }
    if (fromRetainedBest?.selected) {
      return fromRetainedBest;
    }

    const fromUtm = resolveFromKeys(storedKeys);
    if (fromUtm?.selected) {
      return fromUtm;
    }

    const defaultMappings = mappings.filter((m) => normalizeKey(m.channel_identifier) === 'default');
    for (const defaultMapping of defaultMappings) {
      const gNorm = normalizeKey(defaultMapping.selling_plan_group_name);
      const inGroup = recurring.filter((a) => normalizeKey(a.groupName) === gNorm);
      if (inGroup.length) {
        const selected =
          this.#selectDefaultPlanInGroup(inGroup, defaultPlanId) ?? pickCheapestAllocation(inGroup);
        return { allocations: inGroup, selected };
      }
    }

    const cheapestOverall = pickCheapestAllocation(recurring);
    if (cheapestOverall) {
      const sameGroup = recurring.filter(
        (a) => normalizeKey(a.groupName) === normalizeKey(cheapestOverall.groupName)
      );
      const selected =
        this.#selectDefaultPlanInGroup(sameGroup, defaultPlanId) ?? pickCheapestAllocation(sameGroup);
      return { allocations: sameGroup, selected };
    }

    return { allocations: [], selected: undefined };
  }

  /**
   * @param {PlanAllocation[]} allocations
   */
  #fillSelectOptions(allocations) {
    const select = this.#planSelect();
    if (!select) return;
    select.innerHTML = '';
    const usable = filterRecurringAllocations(allocations);
    const sorted = this.#sortAllocationsByGroupOrder(usable);
    for (const a of sorted) {
      const opt = document.createElement('option');
      opt.value = a.sellingPlanId;
      opt.textContent = a.deliveryFrequency || a.sellingPlanName || a.sellingPlanId;
      select.appendChild(opt);
    }
  }

  /**
   * @param {PlanAllocation[]} allocations
   * @param {HTMLElement | null | undefined} frequencyWrap
   * @param {HTMLSelectElement | null | undefined} select
   */
  #updateFrequencyVisibility(allocations, frequencyWrap, select) {
    if (!frequencyWrap) return;
    const optionCount = select?.options.length ?? filterRecurringAllocations(allocations).length;
    if (optionCount <= 1) {
      frequencyWrap.setAttribute('hidden', '');
    } else {
      frequencyWrap.removeAttribute('hidden');
    }
  }

  /**
   * @param {string | number} sellingPlanId
   */
  #syncSellingPlanInput(sellingPlanId) {
    const input = this.#sellingPlanInput();
    if (!input) return;
    input.value = sellingPlanId;
    input.disabled = !sellingPlanId;

    // Companion mode: the filter variant input rides along — disabled
    // (excluded from FormData) whenever no plan is selected, so a one-time
    // purchase adds only the unit.
    const companionVariantInput = this.#companionVariantInput();
    if (companionVariantInput) {
      companionVariantInput.disabled = !sellingPlanId;
    }

    // Direct mode: the Loop-priced marker rides the same toggle, so it is only
    // submitted when a plan is actually selected. Never present in companion
    // mode — that line stays IOM's to discount.
    const loopPricedInput = this.#loopPricedInput();
    if (loopPricedInput) {
      loopPricedInput.disabled = !sellingPlanId;
    }
  }

  /**
   * Companion mode only: dynamic payment buttons (Shop Pay etc.) can only
   * express the form's single item — with the filter subscription selected
   * they would silently check out the unit alone, so hide them. The flag
   * dataset guard ensures we never unhide a block hidden for other reasons
   * (e.g. sold out).
   * @param {'one-time' | 'subscription'} purchaseType
   */
  #syncAcceleratedCheckout(purchaseType) {
    if (!this.#isCompanionMode) return;
    const component = this.closest('product-form-component');
    const accelerated = component?.querySelector('.accelerated-checkout-block');
    if (!(accelerated instanceof HTMLElement)) return;

    if (purchaseType === 'subscription') {
      if (!accelerated.hidden) {
        accelerated.hidden = true;
        accelerated.dataset.channelCompanionHidden = 'true';
      }
    } else if (accelerated.dataset.channelCompanionHidden === 'true') {
      accelerated.hidden = false;
      delete accelerated.dataset.channelCompanionHidden;
    }
  }

  async #refreshPlanState() {
    const variantId = this.#currentVariantId();
    const purchaseType = this.#purchaseType();
    const frequencyWrap = this.#frequencyWrap();
    const select = this.#planSelect();

    if (purchaseType === 'one-time') {
      const subscriptionPreview = this.#resolveSubscriptionSelection(variantId).selected;
      frequencyWrap?.setAttribute('hidden', '');
      select && (select.innerHTML = '');
      this.#syncSellingPlanInput('');
      this.#updateVisiblePricing(subscriptionPreview, variantId, 'one-time');
      this.#syncAcceleratedCheckout('one-time');
      return;
    }

    const { allocations, selected } = this.#resolveSubscriptionSelection(variantId);
    this.#fillSelectOptions(allocations);
    this.#updateFrequencyVisibility(allocations, frequencyWrap, select);

    let plan = selected;
    if (plan && select) {
      const matchOption = Array.from(select.options).some((o) => allocationMatchesSellingPlanId(plan, o.value));
      if (!matchOption) {
        plan = pickCheapestAllocation(allocations);
      }
    }

    if (select && plan) {
      select.value = plan.sellingPlanId;
    }

    if (plan) {
      this.#syncSellingPlanInput(plan.sellingPlanId);
      this.#maybeSaveBestPlan({
        sellingPlanId: plan.sellingPlanId,
        sellingPlanIdNumeric: plan.sellingPlanIdNumeric,
        groupNameNormalized: normalizeKey(plan.groupName),
        price: Number(plan.price),
        updatedAt: Date.now(),
      });
      this.#updateVisiblePricing(plan, variantId, 'subscription');
      this.#syncAcceleratedCheckout('subscription');
    } else {
      const oneTimeRadio = /** @type {HTMLInputElement | null} */ (
        this.querySelector('[data-channel-purchase-type="one-time"]')
      );
      if (oneTimeRadio) {
        oneTimeRadio.checked = true;
        frequencyWrap?.setAttribute('hidden', '');
        select && (select.innerHTML = '');
      }
      this.#syncSellingPlanInput('');
      this.#updateVisiblePricing(undefined, variantId, this.#purchaseType());
      this.#syncAcceleratedCheckout(this.#purchaseType());
    }
  }

  /** @param {Event} event */
  #onVariantUpdate(event) {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const productId = detail?.data?.productId;
    if (productId && String(productId) !== String(this.dataset.productId ?? '')) {
      return;
    }
    window.requestAnimationFrame(() => {
      this.#placeMainPriceAboveVariantPicker();
      void this.#refreshPlanState();
    });
  }

  #onPurchaseTypeChange() {
    void this.#refreshPlanState();
  }

  #onSelectChange() {
    const select = this.#planSelect();
    const id = select?.value ?? '';
    this.#syncSellingPlanInput(id);
    const variantId = this.#currentVariantId();
    const all = this.#allocationsForVariant(variantId);
    const plan = all.find((a) => allocationMatchesSellingPlanId(a, id));
    this.#updateVisiblePricing(plan, variantId, 'subscription');
    if (plan) {
      this.#maybeSaveBestPlan({
        sellingPlanId: plan.sellingPlanId,
        sellingPlanIdNumeric: plan.sellingPlanIdNumeric,
        groupNameNormalized: normalizeKey(plan.groupName),
        price: Number(plan.price),
        updatedAt: Date.now(),
      });
    }
  }
}

if (!customElements.get('channel-selling-plan-root')) {
  customElements.define('channel-selling-plan-root', ChannelSellingPlanRoot);
}
